import axios from 'axios';

// Base API configuration
// const API_BASE_URL = 'http://localhost:5007/api/v1';
const API_BASE_URL = 'https://backend.codewavetechnology.com/api/v1';



// Create axios instance with default configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include credentials in all requests
});

// Create a separate instance for verify endpoint (without Authorization header)
const verifyApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  // Ensure cookies are sent with cross-origin requests
  xsrfCookieName: 'auth_token',
  xsrfHeaderName: 'X-CSRF-TOKEN',
});

// Add request interceptor to verifyApi for debugging
verifyApi.interceptors.request.use(
  (config) => {
    console.log('Verify API request config:', {
      url: config.url,
      withCredentials: config.withCredentials,
      headers: config.headers,
      xsrfCookieName: config.xsrfCookieName,
      xsrfHeaderName: config.xsrfHeaderName
    });
    
    // Manually check if we can access the cookie
    const cookies = document.cookie;
    console.log('All cookies in browser:', cookies);
    const authTokenCookie = cookies.split(';').find(c => c.trim().startsWith('auth_token='));
    console.log('Auth token cookie found:', authTokenCookie);
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for verify API (no redirects)
verifyApi.interceptors.response.use(
  (response) => {
    console.log('Verify API response:', response.data);
    return response;
  },
  (error) => {
    console.log('Verify API error:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });
    // Don't redirect on 401 for verify endpoint, just reject the promise
    return Promise.reject(error);
  }
);

// Request interceptor to add auth token if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Ensure credentials are included for all requests
    config.withCredentials = true;
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('authToken');
      
      // Only redirect if not already on login page and not during verification
      const isVerificationRequest = error.config?.url?.includes('/verify');
      const isLoginPage = window.location.pathname === '/login';
      
      if (!isLoginPage && !isVerificationRequest) {
        // Use a more user-friendly approach
        window.location.href = '/login?error=session_expired';
      }
    }
    return Promise.reject(error);
  }
);

// Authentication API calls
export const authAPI = {
  login: async (email, password) => {
    try {
      const response = await api.post('/login', { email, password }, {
        withCredentials: true,
      });
      console.log('Login response:', response.data);
      console.log('Login response headers:', response.headers);
      
      // Check if Set-Cookie header is present
      const setCookieHeader = response.headers['set-cookie'];
      console.log('Set-Cookie header:', setCookieHeader);
      
      // Check if the response contains a token that should be set as a cookie
      if (response.data.token) {
        console.log('Token received from login:', response.data.token);
        console.log('This token should be set as auth_token cookie by the backend');
      }
      
      // Check cookies after login
      setTimeout(() => {
        console.log('Cookies after login:', document.cookie);
        const authTokenCookie = document.cookie.split(';').find(c => c.trim().startsWith('auth_token='));
        console.log('Auth token cookie after login:', authTokenCookie);
      }, 100);
      
      return response;
    } catch (error) {
      console.log('Login error:', error.response?.data);
      throw error;
    }
  },

  verify: async () => {
    try {
      // Debug: Check what cookies are available
      console.log('Available cookies:', document.cookie);
      console.log('Making verify request...');
      
      const response = await verifyApi.get('/verify');
      return response;
    } catch (error) {
      throw error;
    }
  },

  logout: async () => {
    try {
      const response = await api.post('/logout', {}, {
        withCredentials: true,
      });
      localStorage.removeItem('authToken');
      return response;
    } catch (error) {
      // Even if logout fails on server, clear local token
      localStorage.removeItem('authToken');
      throw error;
    }
  },
};

// Hero Section API calls
export const heroSectionAPI = {
  get: async () => {
    try {
      const response = await api.get('/hero-section', {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  create: async (formData) => {
    try {
      const response = await api.post('/hero-section', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  update: async (formData) => {
    try {
      const response = await api.put('/hero-section', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },
};

// About Us Section API calls
export const aboutUsAPI = {
  get: async () => {
    try {
      const response = await api.get('/about-us', {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  create: async (data) => {
    try {
      const response = await api.post('/about-us', data, {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  update: async (data) => {
    try {
      const response = await api.put('/about-us', data, {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },
};

// Services Section API calls
export const servicesAPI = {
  get: async () => {
    try {
      const response = await api.get('/services', {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  create: async (data) => {
    try {
      const response = await api.post('/services', data, {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  update: async (data) => {
    try {
      const response = await api.put('/services', data, {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },
};

// How It Works Section API calls
export const howItWorksAPI = {
  get: async () => {
    try {
      const response = await api.get('/how-it-works', {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  create: async (data) => {
    try {
      const response = await api.post('/how-it-works', data, {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  update: async (data) => {
    try {
      const response = await api.put('/how-it-works', data, {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },
};

// Our Team Section API calls
export const ourTeamAPI = {
  get: async () => {
    try {
      const response = await api.get('/our-team', {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  create: async (formData) => {
    try {
      const response = await api.post('/our-team', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  update: async (formData) => {
    try {
      const response = await api.put('/our-team', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },
};

// Blogs API calls
export const blogsAPI = {
  get: async (params = {}) => {
    try {
      const response = await api.get('/blogs', {
        params,
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  getById: async (blogId) => {
    try {
      const response = await api.get(`/blogs/${blogId}`, {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  create: async (formData) => {
    try {
      const response = await api.post('/blogs', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  update: async (blogId, formData) => {
    try {
      const response = await api.put(`/blogs/${blogId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  delete: async (blogId) => {
    try {
      const response = await api.delete(`/blogs/${blogId}`, {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  getCategories: async () => {
    try {
      const response = await api.get('/blogs/categories', {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  getAuthors: async () => {
    try {
      const response = await api.get('/blogs/authors', {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  getTags: async () => {
    try {
      const response = await api.get('/blogs/tags', {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },
};

// Testimonials Section API calls
export const testimonialsAPI = {
  get: async () => {
    try {
      const response = await api.get('/testimonials', {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  create: async (formData) => {
    try {
      const response = await api.post('/testimonials', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  update: async (formData) => {
    try {
      const response = await api.put('/testimonials', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },
};

// Image serving helper
export const getImageUrl = (filename) => {
  return `${API_BASE_URL}/images/${filename}`;
};

// Blog categories
export const blogCategories = [
  { id: 'all', name: 'All Posts' },
  { id: 'sms', name: 'Bulk SMS' },
  { id: 'ussd', name: 'USSD Services' },
  { id: 'call-center', name: 'Call Center' },
  { id: 'payments', name: 'Payments' },
  { id: 'software', name: 'Software Dev' }
];

// Contact Management API calls
export const contactsAPI = {
  // Get all contact messages with pagination and filtering
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/admin/contacts', {
        params,
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Get specific contact message with replies
  getById: async (contactId) => {
    try {
      const response = await api.get(`/admin/contacts/${contactId}`, {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Reply to contact message
  reply: async (contactId, formData) => {
    try {
      const response = await api.post(`/admin/contacts/${contactId}/reply`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Update contact status
  updateStatus: async (contactId, status) => {
    try {
      const response = await api.put(`/admin/contacts/${contactId}/status`, 
        { status }, 
        { withCredentials: true }
      );
      return response;
    } catch (error) {
      throw error;
    }
  },
};

// Pricing Section API calls
export const pricingAPI = {
  // Get complete pricing section
  get: async () => {
    try {
      const response = await api.get('/pricing', {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Create complete pricing section
  create: async (data) => {
    try {
      const response = await api.post('/pricing', data, {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Update complete pricing section
  update: async (data) => {
    try {
      const response = await api.put('/pricing', data, {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },
};

// Service Page API calls (singular - matches API documentation)
export const servicesPageAPI = {
  // Get complete service page
  get: async () => {
    try {
      const response = await api.get('/service-page', {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Create complete service page
  create: async (formData) => {
    try {
      const response = await api.post('/service-page', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Update complete service page
  update: async (formData) => {
    try {
      const response = await api.put('/service-page', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },
};

// Debug helper to check cookies
export const debugCookies = () => {
  console.log('=== Cookie Debug Info ===');
  console.log('All cookies:', document.cookie);
  console.log('Auth token cookie:', document.cookie.split(';').find(c => c.trim().startsWith('auth_token=')));
  console.log('Current domain:', window.location.hostname);
  console.log('API base URL:', API_BASE_URL);
  console.log('========================');
};

// Test function to manually set a cookie (for testing)
export const testCookieSetting = () => {
  console.log('🧪 Testing cookie setting...');
  
  // Try to set a test cookie
  document.cookie = 'test_cookie=test_value; path=/; SameSite=None; Secure';
  document.cookie = 'auth_token=test_token_value; path=/; SameSite=None; Secure';
  
  console.log('Cookies after manual setting:', document.cookie);
  
  // Test if we can read it back
  const testCookie = document.cookie.split(';').find(c => c.trim().startsWith('test_cookie='));
  const authCookie = document.cookie.split(';').find(c => c.trim().startsWith('auth_token='));
  
  console.log('Test cookie found:', testCookie);
  console.log('Auth cookie found:', authCookie);
};

// Function to manually set the auth token as a cookie (if backend doesn't do it)
export const setAuthTokenCookie = (token) => {
  if (token) {
    console.log('Setting auth_token cookie manually:', token);
    // Set cookie with proper attributes for cross-origin requests
    document.cookie = `auth_token=${token}; path=/; SameSite=None; Secure; HttpOnly=false`;
    console.log('Cookie set, current cookies:', document.cookie);
  }
};

// Content Approval API calls (Super Admin Only)
export const approvalAPI = {
  // Get all pending content
  getPendingContent: async (contentType = null) => {
    try {
      const params = contentType ? { content_type: contentType } : {};
      const response = await api.get('/admin/content/pending', {
        params,
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Get detailed content for approval (with diff comparison)
  getContentDetails: async (contentType, contentId) => {
    try {
      const response = await api.get(`/admin/content/${contentType}/${contentId}/details`, {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Approve content
  approveContent: async (contentType, contentId) => {
    try {
      const response = await api.post(`/admin/content/${contentType}/${contentId}/approve`, {}, {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Reject content
  rejectContent: async (contentType, contentId) => {
    try {
      const response = await api.post(`/admin/content/${contentType}/${contentId}/reject`, {}, {
        withCredentials: true,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },
};

// Generic API methods for future use
export const apiMethods = {
  get: (url, config = {}) => api.get(url, { ...config, withCredentials: true }),
  post: (url, data = {}, config = {}) => api.post(url, data, { ...config, withCredentials: true }),
  put: (url, data = {}, config = {}) => api.put(url, data, { ...config, withCredentials: true }),
  patch: (url, data = {}, config = {}) => api.patch(url, data, { ...config, withCredentials: true }),
  delete: (url, config = {}) => api.delete(url, { ...config, withCredentials: true }),
};

export default api;
